import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import ReadOnlyJsonField from './ReadOnlyJsonField';

/**
 * Read-only properties panel for the currently open FLEX script -- version/
 * domain/category/extends plus input_schema/output_schema. The schemas are
 * derived automatically (from @requires/@optional/@returns directives, or
 * from a real sample execution for output_schema -- see
 * FlexScriptService.buildInputSchema/backfillSampleOutputSchemas on the
 * backend) rather than hand-edited here.
 */
function FlexScriptProperties({ open, onClose, script }) {
  if (!script) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Script Properties
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Name</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{script.name}</Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Version</Typography>
              <Typography variant="body2">{script.version || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Domain</Typography>
              <Typography variant="body2">{script.domain || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Category</Typography>
              <Typography variant="body2">{script.category || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Trigger Type</Typography>
              <Typography variant="body2">{script.triggerType || '—'}</Typography>
            </Box>
          </Box>

          {script.extendsScript && (
            <Box>
              <Typography variant="caption" color="text.secondary">Extends</Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  size="small"
                  label={`${script.extendsScript}${script.extendsScope ? ` (${script.extendsScope})` : ''}`}
                  sx={{ fontFamily: 'monospace' }}
                />
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" color="text.secondary">
            Schema (auto-derived from @requires/@optional/@returns and, for
            output_schema, a real sample run -- not editable here)
          </Typography>

          <ReadOnlyJsonField label="Input Schema" value={script.inputSchema} />
          <ReadOnlyJsonField label="Output Schema" value={script.outputSchema} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default FlexScriptProperties;
